/**
 * Bot Create Component
 *
 * Provides a form for creating new Discord bots with:
 * - Basic settings (name, description, Discord token)
 * - LiteLLM endpoint selection for initial configuration
 * - Auto-start and auto-restart toggles
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant, type Tenant } from '../../contexts/TenantContext';
import { getApiClient, isSuccess } from '../../api/client';
import { Layout } from '../layout/Layout';

// =============================================================================
// Types
// =============================================================================

/**
 * Endpoint type enum matching backend
 */
type EndpointType = 'LLM' | 'STT' | 'TTS' | 'IMAGE';

/**
 * LiteLLM endpoint dropdown item
 */
interface EndpointDropdownItem {
  id: string;
  name: string;
  modelName: string;
  backend: string;
  isGlobal: boolean;
  priority: number;
}

/**
 * Endpoints grouped by type for dropdowns
 */
interface EndpointsByType {
  LLM: EndpointDropdownItem[];
  STT: EndpointDropdownItem[];
  TTS: EndpointDropdownItem[];
  IMAGE: EndpointDropdownItem[];
}

/**
 * Bot configuration structure
 */
interface BotConfig {
  llmEndpointId?: string;
  ttsEndpointId?: string;
  sttEndpointId?: string;
  imageEndpointId?: string;
  voiceProfileId?: string;
  systemPrompt?: string;
  memorySize?: number;
  wakePhrase?: string;
  voiceTriggerEnabled?: boolean;
  voiceIdleTimeout?: number;
}

/**
 * Form data for bot creation
 */
interface CreateBotFormData {
  name: string;
  description: string;
  discordToken: string;
  autoStart: boolean;
  autoRestart: boolean;
  config: BotConfig;
}

/**
 * Form validation errors
 */
interface FormErrors {
  name?: string;
  discordToken?: string;
  general?: string;
}

/**
 * Voice profile
 */
interface VoiceProfile {
  id: string;
  name: string;
  isGlobal: boolean;
}

// =============================================================================
// Constants
// =============================================================================

const DEFAULT_FORM_DATA: CreateBotFormData = {
  name: '',
  description: '',
  discordToken: '',
  autoStart: false,
  autoRestart: true,
  config: {
    memorySize: 20,
    voiceTriggerEnabled: true,
    voiceIdleTimeout: 300,
    wakePhrase: 'hey bot',
    systemPrompt: '',
  },
};

// =============================================================================
// Sub-components
// =============================================================================

/**
 * Form section component
 */
interface FormSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
}

function FormSection({ title, description, children }: FormSectionProps) {
  return (
    <div className="rounded-lg bg-discord-darker border border-discord-light p-6">
      <h3 className="text-lg font-semibold text-white mb-1">{title}</h3>
      {description && (
        <p className="text-sm text-gray-400 mb-4">{description}</p>
      )}
      <div className="space-y-4">{children}</div>
    </div>
  );
}

/**
 * Form input component
 */
interface FormInputProps {
  label: string;
  id: string;
  type?: 'text' | 'password' | 'number' | 'textarea';
  value: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  helpText?: string;
  error?: string;
  min?: number;
  max?: number;
  rows?: number;
}

function FormInput({
  label,
  id,
  type = 'text',
  value,
  onChange,
  placeholder,
  required,
  disabled,
  helpText,
  error,
  min,
  max,
  rows = 3,
}: FormInputProps) {
  const inputClasses = `w-full rounded-md border bg-discord-darkest py-2 px-3 text-white placeholder-gray-500 focus:outline-none focus:ring-1 ${
    error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : 'border-discord-light focus:border-discord-blurple focus:ring-discord-blurple'
  } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`;

  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>
      {type === 'textarea' ? (
        <textarea
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          rows={rows}
          className={inputClasses}
        />
      ) : (
        <input
          type={type}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          disabled={disabled}
          min={min}
          max={max}
          className={inputClasses}
        />
      )}
      {helpText && !error && (
        <p className="mt-1 text-xs text-gray-500">{helpText}</p>
      )}
      {error && <p className="mt-1 text-xs text-red-400">{error}</p>}
    </div>
  );
}

/**
 * Form toggle component
 */
interface FormToggleProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function FormToggle({ label, description, checked, onChange, disabled }: FormToggleProps) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <span className="text-sm font-medium text-gray-300">{label}</span>
        {description && (
          <p className="text-xs text-gray-500">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-discord-blurple' : 'bg-discord-light'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

/**
 * Endpoint select dropdown component
 */
interface EndpointSelectProps {
  label: string;
  type: EndpointType;
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  endpoints: EndpointDropdownItem[];
  isLoading: boolean;
  disabled?: boolean;
  helpText?: string;
}

function EndpointSelect({
  label,
  type,
  value,
  onChange,
  endpoints,
  isLoading,
  disabled,
  helpText,
}: EndpointSelectProps) {
  const typeLabel = {
    LLM: 'Language Model',
    STT: 'Speech-to-Text',
    TTS: 'Text-to-Speech',
    IMAGE: 'Image Generation',
  }[type];

  // Group endpoints by global vs tenant-specific
  const groupedEndpoints = useMemo(() => {
    const global = endpoints.filter((e) => e.isGlobal);
    const tenant = endpoints.filter((e) => !e.isGlobal);
    return { global, tenant };
  }, [endpoints]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">
        {label}
        <span className="text-gray-500 text-xs ml-2">({typeLabel})</span>
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={disabled || isLoading}
        className={`w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple ${
          disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <option value="">-- Select {typeLabel} --</option>
        {groupedEndpoints.tenant.length > 0 && (
          <optgroup label="Tenant Endpoints">
            {groupedEndpoints.tenant.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name} ({endpoint.modelName} - {endpoint.backend})
              </option>
            ))}
          </optgroup>
        )}
        {groupedEndpoints.global.length > 0 && (
          <optgroup label="Global Endpoints">
            {groupedEndpoints.global.map((endpoint) => (
              <option key={endpoint.id} value={endpoint.id}>
                {endpoint.name} ({endpoint.modelName} - {endpoint.backend})
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {helpText && <p className="mt-1 text-xs text-gray-500">{helpText}</p>}
      {isLoading && (
        <p className="mt-1 text-xs text-gray-500">Loading endpoints...</p>
      )}
    </div>
  );
}

/**
 * Voice profile select dropdown component
 */
interface VoiceProfileSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  profiles: VoiceProfile[];
  isLoading: boolean;
  disabled?: boolean;
}

function VoiceProfileSelect({
  value,
  onChange,
  profiles,
  isLoading,
  disabled,
}: VoiceProfileSelectProps) {
  const groupedProfiles = useMemo(() => {
    const global = profiles.filter((p) => p.isGlobal);
    const tenant = profiles.filter((p) => !p.isGlobal);
    return { global, tenant };
  }, [profiles]);

  return (
    <div>
      <label className="block text-sm font-medium text-gray-300 mb-1.5">
        Voice Profile
        <span className="text-gray-500 text-xs ml-2">(for TTS)</span>
      </label>
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || undefined)}
        disabled={disabled || isLoading}
        className={`w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple ${
          disabled || isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        <option value="">-- Default Voice --</option>
        {groupedProfiles.tenant.length > 0 && (
          <optgroup label="Tenant Voices">
            {groupedProfiles.tenant.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </optgroup>
        )}
        {groupedProfiles.global.length > 0 && (
          <optgroup label="Global Voices">
            {groupedProfiles.global.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name}
              </option>
            ))}
          </optgroup>
        )}
      </select>
      {isLoading && (
        <p className="mt-1 text-xs text-gray-500">Loading voice profiles...</p>
      )}
    </div>
  );
}

/**
 * Step indicator component
 */
interface StepIndicatorProps {
  currentStep: number;
  totalSteps: number;
  labels: string[];
}

function StepIndicator({ currentStep, totalSteps, labels }: StepIndicatorProps) {
  return (
    <div className="flex items-center justify-center gap-2 mb-6">
      {labels.map((label, index) => (
        <div key={label} className="flex items-center">
          <div
            className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
              index < currentStep
                ? 'bg-green-600 text-white'
                : index === currentStep
                  ? 'bg-discord-blurple text-white'
                  : 'bg-discord-light text-gray-400'
            }`}
          >
            {index < currentStep ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              index + 1
            )}
          </div>
          <span
            className={`ml-2 text-sm ${
              index <= currentStep ? 'text-white' : 'text-gray-500'
            }`}
          >
            {label}
          </span>
          {index < totalSteps - 1 && (
            <div
              className={`w-12 h-0.5 mx-2 ${
                index < currentStep ? 'bg-green-600' : 'bg-discord-light'
              }`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Bot creation page component
 *
 * Provides a multi-step form for creating new Discord bots with:
 * - Step 1: Basic information (name, description, token)
 * - Step 2: AI service configuration (endpoints)
 * - Step 3: Review and create
 */
export function BotCreate() {
  const navigate = useNavigate();
  useAuth(); // Ensure user is authenticated
  const { effectiveTenantId, isGlobalAdmin, availableTenants } = useTenant();

  // State
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<CreateBotFormData>(DEFAULT_FORM_DATA);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string | null>(effectiveTenantId);
  const [isTenantsLoading, setIsTenantsLoading] = useState(false);
  const [endpoints, setEndpoints] = useState<EndpointsByType>({
    LLM: [],
    STT: [],
    TTS: [],
    IMAGE: [],
  });
  const [voiceProfiles, setVoiceProfiles] = useState<VoiceProfile[]>([]);
  const [isEndpointsLoading, setIsEndpointsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showToken, setShowToken] = useState(false);

  const stepLabels = ['Basic Info', 'AI Services', 'Review'];

  /**
   * Fetch LiteLLM endpoints for dropdowns
   */
  const fetchEndpoints = useCallback(async () => {
    setIsEndpointsLoading(true);

    const apiClient = getApiClient();

    try {
      const result = await apiClient.get<EndpointsByType>('/litellm/endpoints/by-type');

      if (isSuccess(result)) {
        setEndpoints(result.data);
      }
    } catch {
      // Endpoints are optional
    } finally {
      setIsEndpointsLoading(false);
    }
  }, []);

  /**
   * Fetch voice profiles
   */
  const fetchVoiceProfiles = useCallback(async () => {
    const apiClient = getApiClient();

    try {
      const queryParams = effectiveTenantId
        ? `?tenantId=${effectiveTenantId}`
        : '';
      const result = await apiClient.get<{ profiles: VoiceProfile[] }>(
        `/voices${queryParams}`
      );

      if (isSuccess(result)) {
        setVoiceProfiles(result.data.profiles || []);
      }
    } catch {
      // Voice profiles are optional
    }
  }, [effectiveTenantId]);

  /**
   * Fetch tenants for global admins
   */
  const fetchTenants = useCallback(async () => {
    if (!isGlobalAdmin) return;

    setIsTenantsLoading(true);
    const apiClient = getApiClient();

    try {
      const result = await apiClient.get<{ tenants: Tenant[] }>('/tenants');

      if (isSuccess(result)) {
        setTenants(result.data.tenants || []);
      }
    } catch {
      // Use available tenants from context as fallback
      if (availableTenants.length > 0) {
        setTenants(availableTenants);
      }
    } finally {
      setIsTenantsLoading(false);
    }
  }, [isGlobalAdmin, availableTenants]);

  /**
   * Validate current step
   */
  const validateStep = useCallback((step: number): boolean => {
    const newErrors: FormErrors = {};

    if (step === 0) {
      if (!formData.name.trim()) {
        newErrors.name = 'Bot name is required';
      } else if (formData.name.length > 100) {
        newErrors.name = 'Bot name must be at most 100 characters';
      }

      if (!formData.discordToken.trim()) {
        newErrors.discordToken = 'Discord token is required';
      } else if (formData.discordToken.length < 50) {
        newErrors.discordToken = 'Invalid Discord token format';
      }

      // Global admins must select a tenant
      if (isGlobalAdmin && !selectedTenantId) {
        newErrors.general = 'Please select a tenant for this bot';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, isGlobalAdmin, selectedTenantId]);

  /**
   * Handle next step
   */
  const handleNextStep = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(prev + 1, stepLabels.length - 1));
    }
  }, [currentStep, validateStep, stepLabels.length]);

  /**
   * Handle previous step
   */
  const handlePrevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    setErrors({});
  }, []);

  /**
   * Create bot
   */
  const handleCreate = useCallback(async () => {
    if (!validateStep(currentStep)) return;

    // Determine which tenant ID to use
    const tenantIdToUse = isGlobalAdmin ? selectedTenantId : effectiveTenantId;

    // Check tenant requirement
    if (!tenantIdToUse) {
      setErrors({ general: 'Please select a tenant before creating a bot' });
      return;
    }

    setIsCreating(true);
    setErrors({});

    const apiClient = getApiClient();

    try {
      const queryParams = `?tenantId=${tenantIdToUse}`;

      const result = await apiClient.post<{ id: string }>(
        `/bots${queryParams}`,
        {
          name: formData.name,
          description: formData.description || undefined,
          discordToken: formData.discordToken,
          autoStart: formData.autoStart,
          autoRestart: formData.autoRestart,
          config: formData.config,
        }
      );

      if (isSuccess(result)) {
        // Navigate to the new bot's config page
        const botId = result.data.id || (result.data as unknown as { bot: { id: string } }).bot?.id;
        navigate(`/bots/${botId}/config`);
      } else {
        setErrors({ general: result.error.message || 'Failed to create bot' });
      }
    } catch (err) {
      setErrors({ general: 'Failed to create bot. Please try again.' });
    } finally {
      setIsCreating(false);
    }
  }, [formData, effectiveTenantId, selectedTenantId, isGlobalAdmin, currentStep, navigate, validateStep]);

  /**
   * Handle form field changes
   */
  const handleFieldChange = useCallback(
    (field: keyof CreateBotFormData, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      // Clear related error
      if (field === 'name' || field === 'discordToken') {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    []
  );

  /**
   * Handle config field changes
   */
  const handleConfigChange = useCallback(
    (field: keyof BotConfig, value: string | number | boolean | undefined) => {
      setFormData((prev) => ({
        ...prev,
        config: { ...prev.config, [field]: value },
      }));
    },
    []
  );

  /**
   * Initial data fetch
   */
  useEffect(() => {
    void fetchEndpoints();
    void fetchVoiceProfiles();
    void fetchTenants();
  }, [fetchEndpoints, fetchVoiceProfiles, fetchTenants]);

  /**
   * Sync selected tenant with effective tenant when it changes
   */
  useEffect(() => {
    if (effectiveTenantId && !selectedTenantId) {
      setSelectedTenantId(effectiveTenantId);
    }
  }, [effectiveTenantId, selectedTenantId]);

  /**
   * Get selected endpoint name
   */
  const getEndpointName = (type: EndpointType, id?: string): string => {
    if (!id) return 'Not configured';
    const endpoint = endpoints[type].find((e) => e.id === id);
    return endpoint ? `${endpoint.name} (${endpoint.modelName})` : 'Unknown';
  };

  /**
   * Get voice profile name
   */
  const getVoiceProfileName = (id?: string): string => {
    if (!id) return 'Default';
    const profile = voiceProfiles.find((p) => p.id === id);
    return profile ? profile.name : 'Unknown';
  };

  /**
   * Get tenant name
   */
  const getTenantName = (id?: string | null): string => {
    if (!id) return 'Not selected';
    const tenant = tenants.find((t) => t.id === id);
    return tenant ? tenant.name : 'Unknown';
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/bots')}
            className="rounded-md bg-discord-light p-2 text-gray-400 hover:text-white transition-colors"
            title="Back to bots"
          >
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Create New Bot</h1>
            <p className="text-sm text-gray-400">
              Set up a new Discord bot with AI capabilities
            </p>
          </div>
        </div>

        {/* Step Indicator */}
        <StepIndicator
          currentStep={currentStep}
          totalSteps={stepLabels.length}
          labels={stepLabels}
        />

        {/* Error Alert */}
        {errors.general && (
          <div className="rounded-md bg-red-900/50 border border-red-500 p-4">
            <div className="flex items-start">
              <svg
                className="mr-3 h-5 w-5 flex-shrink-0 text-red-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="flex-1">
                <p className="text-sm text-red-200">{errors.general}</p>
              </div>
              <button
                onClick={() => setErrors((prev) => ({ ...prev, general: undefined }))}
                className="ml-3 text-sm font-medium text-red-400 hover:text-red-300"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}

        {/* Step 1: Basic Info */}
        {currentStep === 0 && (
          <FormSection
            title="Basic Information"
            description="Enter the essential details for your Discord bot"
          >
            {/* Tenant selector for global admins */}
            {isGlobalAdmin && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                  Tenant
                  <span className="text-red-400 ml-1">*</span>
                </label>
                <select
                  value={selectedTenantId || ''}
                  onChange={(e) => setSelectedTenantId(e.target.value || null)}
                  disabled={isTenantsLoading}
                  className={`w-full rounded-md border border-discord-light bg-discord-darkest py-2 px-3 text-white focus:border-discord-blurple focus:outline-none focus:ring-1 focus:ring-discord-blurple ${
                    isTenantsLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <option value="">-- Select Tenant --</option>
                  {tenants.map((tenant) => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name} ({tenant.slug})
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select the tenant this bot will belong to
                </p>
                {isTenantsLoading && (
                  <p className="mt-1 text-xs text-gray-500">Loading tenants...</p>
                )}
              </div>
            )}
            <FormInput
              label="Bot Name"
              id="name"
              value={formData.name}
              onChange={(value) => handleFieldChange('name', value)}
              placeholder="My Discord Bot"
              required
              error={errors.name}
            />
            <FormInput
              label="Description"
              id="description"
              type="textarea"
              value={formData.description}
              onChange={(value) => handleFieldChange('description', value)}
              placeholder="A helpful bot for my server..."
              rows={2}
            />
            <div>
              <FormInput
                label="Discord Token"
                id="discordToken"
                type={showToken ? 'text' : 'password'}
                value={formData.discordToken}
                onChange={(value) => handleFieldChange('discordToken', value)}
                placeholder="Enter your Discord bot token"
                required
                error={errors.discordToken}
                helpText="Get this from the Discord Developer Portal"
              />
              <button
                type="button"
                onClick={() => setShowToken(!showToken)}
                className="mt-1 text-xs text-discord-blurple hover:text-discord-blurple/80"
              >
                {showToken ? 'Hide' : 'Show'} token
              </button>
            </div>
            <FormToggle
              label="Auto Start"
              description="Automatically start bot when the system starts"
              checked={formData.autoStart}
              onChange={(checked) => handleFieldChange('autoStart', checked)}
            />
            <FormToggle
              label="Auto Restart"
              description="Automatically restart bot if it crashes"
              checked={formData.autoRestart}
              onChange={(checked) => handleFieldChange('autoRestart', checked)}
            />
          </FormSection>
        )}

        {/* Step 2: AI Services */}
        {currentStep === 1 && (
          <FormSection
            title="AI Service Configuration"
            description="Configure which AI services your bot will use"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <EndpointSelect
                label="LLM Endpoint"
                type="LLM"
                value={formData.config.llmEndpointId}
                onChange={(value) => handleConfigChange('llmEndpointId', value)}
                endpoints={endpoints.LLM}
                isLoading={isEndpointsLoading}
                helpText="Language model for chat responses"
              />
              <EndpointSelect
                label="TTS Endpoint"
                type="TTS"
                value={formData.config.ttsEndpointId}
                onChange={(value) => handleConfigChange('ttsEndpointId', value)}
                endpoints={endpoints.TTS}
                isLoading={isEndpointsLoading}
                helpText="Text-to-speech for voice responses"
              />
              <EndpointSelect
                label="STT Endpoint"
                type="STT"
                value={formData.config.sttEndpointId}
                onChange={(value) => handleConfigChange('sttEndpointId', value)}
                endpoints={endpoints.STT}
                isLoading={isEndpointsLoading}
                helpText="Speech-to-text for voice input"
              />
              <EndpointSelect
                label="Image Endpoint"
                type="IMAGE"
                value={formData.config.imageEndpointId}
                onChange={(value) => handleConfigChange('imageEndpointId', value)}
                endpoints={endpoints.IMAGE}
                isLoading={isEndpointsLoading}
                helpText="Image generation capabilities"
              />
            </div>
            <VoiceProfileSelect
              value={formData.config.voiceProfileId}
              onChange={(value) => handleConfigChange('voiceProfileId', value)}
              profiles={voiceProfiles}
              isLoading={isEndpointsLoading}
            />
            <FormInput
              label="Wake Phrase"
              id="wakePhrase"
              value={formData.config.wakePhrase || ''}
              onChange={(value) => handleConfigChange('wakePhrase', value)}
              placeholder="hey bot"
              helpText="Phrase to trigger the bot in voice channels"
            />
            <FormInput
              label="Memory Size"
              id="memorySize"
              type="number"
              value={formData.config.memorySize || 20}
              onChange={(value) => handleConfigChange('memorySize', parseInt(value, 10) || 20)}
              min={1}
              max={100}
              helpText="Number of conversation messages to remember (1-100)"
            />
            <FormInput
              label="System Prompt"
              id="systemPrompt"
              type="textarea"
              value={formData.config.systemPrompt || ''}
              onChange={(value) => handleConfigChange('systemPrompt', value)}
              placeholder="You are a helpful assistant..."
              rows={3}
              helpText="Initial instructions for the AI model"
            />
          </FormSection>
        )}

        {/* Step 3: Review */}
        {currentStep === 2 && (
          <FormSection
            title="Review Configuration"
            description="Review your bot configuration before creating"
          >
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="border-b border-discord-light pb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">Basic Information</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  {isGlobalAdmin && (
                    <>
                      <span className="text-gray-500">Tenant:</span>
                      <span className="text-white">{getTenantName(selectedTenantId)}</span>
                    </>
                  )}
                  <span className="text-gray-500">Name:</span>
                  <span className="text-white">{formData.name}</span>
                  <span className="text-gray-500">Description:</span>
                  <span className="text-white">{formData.description || 'None'}</span>
                  <span className="text-gray-500">Auto Start:</span>
                  <span className="text-white">{formData.autoStart ? 'Yes' : 'No'}</span>
                  <span className="text-gray-500">Auto Restart:</span>
                  <span className="text-white">{formData.autoRestart ? 'Yes' : 'No'}</span>
                </div>
              </div>

              {/* AI Services */}
              <div className="border-b border-discord-light pb-4">
                <h4 className="text-sm font-medium text-gray-400 mb-2">AI Services</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">LLM:</span>
                  <span className="text-white">
                    {getEndpointName('LLM', formData.config.llmEndpointId)}
                  </span>
                  <span className="text-gray-500">TTS:</span>
                  <span className="text-white">
                    {getEndpointName('TTS', formData.config.ttsEndpointId)}
                  </span>
                  <span className="text-gray-500">STT:</span>
                  <span className="text-white">
                    {getEndpointName('STT', formData.config.sttEndpointId)}
                  </span>
                  <span className="text-gray-500">Image:</span>
                  <span className="text-white">
                    {getEndpointName('IMAGE', formData.config.imageEndpointId)}
                  </span>
                </div>
              </div>

              {/* Voice Settings */}
              <div>
                <h4 className="text-sm font-medium text-gray-400 mb-2">Voice Settings</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">Voice Profile:</span>
                  <span className="text-white">
                    {getVoiceProfileName(formData.config.voiceProfileId)}
                  </span>
                  <span className="text-gray-500">Wake Phrase:</span>
                  <span className="text-white">{formData.config.wakePhrase || 'None'}</span>
                  <span className="text-gray-500">Memory Size:</span>
                  <span className="text-white">{formData.config.memorySize} messages</span>
                </div>
              </div>
            </div>
          </FormSection>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <button
            onClick={currentStep === 0 ? () => navigate('/bots') : handlePrevStep}
            className="rounded-md bg-discord-light px-4 py-2 text-sm font-medium text-gray-300 hover:text-white transition-colors"
          >
            {currentStep === 0 ? 'Cancel' : 'Back'}
          </button>
          <div className="flex gap-3">
            {currentStep < stepLabels.length - 1 ? (
              <button
                onClick={handleNextStep}
                className="rounded-md bg-discord-blurple px-4 py-2 text-sm font-medium text-white hover:bg-discord-blurple/90 transition-colors"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleCreate}
                disabled={isCreating}
                className="rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? 'Creating...' : 'Create Bot'}
              </button>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default BotCreate;
